import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Star, ExternalLink, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GoogleReviews = () => {
  const [reviewData, setReviewData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/reviews`);
        if (res.data.success) {
          setReviewData(res.data);
        }
      } catch (err) {
        console.error('Failed to fetch reviews:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  if (loading) return null;

  const rating = reviewData?.rating || 5.0;
  const totalReviews = reviewData?.total_reviews || 0;
  const reviews = reviewData?.reviews || [];
  const mapsUrl = reviewData?.google_maps_url || '#';

  const renderStars = (count) => (
    <div className="flex gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`w-5 h-5 ${i < count ? 'fill-[#FBBC04] text-[#FBBC04]' : 'text-gray-300'}`}
        />
      ))}
    </div>
  );

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (timeStr) => {
    if (!timeStr) return '';
    try {
      const date = new Date(timeStr);
      if (isNaN(date.getTime())) return timeStr;
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    } catch {
      return timeStr;
    }
  };

  return (
    <section id="testimonials" className="py-20 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Header with Google badge */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-[rgb(0,55,32)] mb-4" data-testid="reviews-title">
            What Our Clients Say
          </h2>
          <p className="text-lg text-[rgb(131,146,140)] max-w-2xl mx-auto mb-8">
            Real reviews from real homeowners
          </p>

          {/* Google Rating Badge */}
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-white border border-[rgba(0,0,0,0.12)] rounded-2xl px-6 py-4 shadow-sm hover:shadow-md transition-shadow"
            data-testid="google-rating-badge"
          >
            <svg viewBox="0 0 24 24" className="w-8 h-8" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <div className="text-left">
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-[rgb(14,15,12)]">{rating}</span>
                {renderStars(Math.round(rating))}
              </div>
              <span className="text-sm text-[rgb(131,146,140)]">
                Based on {totalReviews} Google reviews
              </span>
            </div>
          </a>
        </div>

        {/* Reviews Grid */}
        {reviews.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {reviews.map((review, index) => (
              <Card
                key={index}
                className="p-6 bg-white border-[rgba(0,0,0,0.1)] rounded-xl hover:shadow-lg transition-shadow"
                data-testid={`review-card-${index}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  {review.profile_photo_url ? (
                    <img
                      src={review.profile_photo_url}
                      alt={review.author_name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] flex items-center justify-center text-white font-bold text-sm">
                      {getInitials(review.author_name)}
                    </div>
                  )}
                  <div>
                    <div className="font-semibold text-[rgb(0,55,32)]">{review.author_name}</div>
                    {review.time && (
                      <div className="text-xs text-[rgb(131,146,140)]">{formatDate(review.time)}</div>
                    )}
                  </div>
                  <svg viewBox="0 0 24 24" className="w-5 h-5 ml-auto flex-shrink-0" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                </div>
                {renderStars(review.rating)}
                {review.text && (
                  <p className="mt-3 text-[rgb(14,15,12)] leading-relaxed text-sm">
                    "{review.text}"
                  </p>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="see-all-reviews-btn"
          >
            <Button
              className="bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-strong)] text-white font-semibold h-12 px-8 rounded-full hover:scale-102 transition-transform"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              See All {totalReviews} Reviews on Google
            </Button>
          </a>
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="leave-review-btn"
          >
            <Button
              variant="outline"
              className="h-12 px-8 rounded-full border-[rgba(0,0,0,0.2)] hover:bg-[rgba(0,0,0,0.05)] font-semibold"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Leave Us a Review
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviews;
